import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, MoreHorizontal, Settings, AlertCircle, Calculator, Loader2, Zap, Link, Copy, ExternalLink, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { 
  usePricingTiers, 
  useCreatePricingTier, 
  useUpdatePricingTier, 
  useDeletePricingTier,
  usePlanSubscribers,
  usePartnerSubscriptionLinks,
} from '@/hooks/usePartnerSettings';
import { usePartnerDetails } from '@/hooks/usePartnerDetails';
import { usePlans, useUpdatePlan, useDeletePlan } from '@/hooks/usePartnerPlans';
import { usePartnerSubAccountCreation } from '@/hooks/usePartnerSubAccountCreation';

import CreatePlanDialog from './pricing/CreatePlanDialog.tsx';
import { CreatePricingTierData, PartnerSubscriptionPlan, PricingTier, UpdatePartnerSubscriptionPlanData } from '@/types/partners';

function CustomPricing() {
  // Get partner details to check if white-label
  const { data: partnerDetails } = usePartnerDetails();
  const isWhiteLabel = partnerDetails?.partner_type === 'white_label';
  
  // Get boost pack pricing status for white-label partners
  const { hasCustomBoostPackPricing } = usePartnerSubAccountCreation();

  // Real data from hooks
  const { data: pricingTiers, isLoading: pricingLoading, error: pricingError } = usePricingTiers();
  const { data: subscriptionPlans, isLoading: plansLoading, error: plansError } = usePlans();
  
  // Fetch subscription links with permanent checkout links
  const { refetch: refetchSubscriptionLinks } = usePartnerSubscriptionLinks();
  
  // Mutations
  const createPricingTier = useCreatePricingTier();
  const updatePricingTier = useUpdatePricingTier();
  const deletePricingTier = useDeletePricingTier();

  const updatePlan = useUpdatePlan();
  const deletePlan = useDeletePlan();

  // State for create pricing tier dialog
  const [isCreateTierOpen, setIsCreateTierOpen] = useState(false);
  const [tierPrice, setTierPrice] = useState('');

  // State for edit plan dialog
  const [isEditPlanOpen, setIsEditPlanOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PartnerSubscriptionPlan | null>(null);
  const [editPlanName, setEditPlanName] = useState('');
  const [editPlanDescription, setEditPlanDescription] = useState('');
  const [editPlanCredits, setEditPlanCredits] = useState('');
  const [editPlanPrice, setEditPlanPrice] = useState('');
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);
  const [editPlanNameError, setEditPlanNameError] = useState('');

  // State for view subscribers dialog
  const [isViewSubscribersOpen, setIsViewSubscribersOpen] = useState(false);
  const [selectedPlanForSubscribers, setSelectedPlanForSubscribers] = useState<PartnerSubscriptionPlan | null>(null);

  // State for delete confirmation dialog
  const [isDeletePlanOpen, setIsDeletePlanOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<{ id: string; name: string } | null>(null);

  // State for dropdown menus
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // State for subscription link display
  const [isGenerateLinkOpen, setIsGenerateLinkOpen] = useState(false);
  const [selectedPlanForLink, setSelectedPlanForLink] = useState<PartnerSubscriptionPlan | null>(null);
  const [generatedLink, setGeneratedLink] = useState('');
  const [isLinkLoading, setIsLinkLoading] = useState(false);

  // Subscribers data
  const { data: subscribers, isLoading: subscribersLoading } = usePlanSubscribers(selectedPlanForSubscribers?.id || null);

  // State for edit tier dialog
  const [isEditTierOpen, setIsEditTierOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<PricingTier | null>(null);
  const [editTierName, setEditTierName] = useState('');
  const [editTierCredits, setEditTierCredits] = useState('');
  const [editTierPrice, setEditTierPrice] = useState('');
  const [editTierBadge, setEditTierBadge] = useState('');
  const [isEditTierFeatured, setIsEditTierFeatured] = useState(false);

  // State for delete confirmation dialog
  const [isDeleteTierOpen, setIsDeleteTierOpen] = useState(false);
  const [tierToDelete, setTierToDelete] = useState<{ id: string; name: string } | null>(null);

  const calculatePlanMargin = (credits: number, price: number) => {
    const baseCost = credits * 0.38; // Average $0.38 per call
    return price > 0 ? ((price - baseCost) / price * 100).toFixed(1) : '0';
  };

  const calculateTierMargin = (credits: number, price: number) => {
    const baseCost = credits * 0.19; // $0.19 per minute base cost
    return price > 0 ? ((price - baseCost) / price * 100).toFixed(1) : '0';
  };


  const handleCreateBoostPackRate = async () => {
    if (!tierPrice || parseFloat(tierPrice) < 0.19) {
      toast.error('Please enter a valid rate (minimum $0.19)');
      return;
    }

    try {
      // Check if per_credit_rate tier already exists
      const existingTier = pricingTiers?.find(tier => tier.tier_name === 'per_credit_rate');
      
      const rateData: CreatePricingTierData & { id?: string } = {
        tier_name: 'per_credit_rate',
        credits: 1, // Store as 1 credit to represent per-credit rate
        price_usd: parseFloat(tierPrice), // This is the per-credit rate
        is_featured: false,
        badge_text: undefined,
        // Include ID if updating existing tier
        ...(existingTier && { id: existingTier.id })
      };

      await createPricingTier.mutateAsync(rateData);
      
      const action = existingTier ? 'updated' : 'set';
      toast.success(`Boost pack rate ${action} to $${parseFloat(tierPrice).toFixed(4)} per credit`);
      setIsCreateTierOpen(false);
      setTierPrice('');
    } catch (error) {
      toast.error(`Failed to set rate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleEditBoostPackRate = (tier: any) => {
    setTierPrice(tier.price_usd.toString());
    setOpenDropdownId(null); 
    setIsCreateTierOpen(true);
  };


  const handleUpdateTier = async () => {
    if (!editingTier || !editTierName || !editTierCredits || !editTierPrice) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const updateData = {
        tier_name: editTierName,
        credits: parseInt(editTierCredits),
        price_usd: parseFloat(editTierPrice),
        is_featured: isEditTierFeatured,
        badge_text: editTierBadge || undefined,
      };

      await updatePricingTier.mutateAsync({ id: editingTier.id, data: updateData });
      
      toast.success(`Pricing tier "${editTierName}" updated successfully`);
      setIsEditTierOpen(false);
      setEditingTier(null);
      setEditTierName('');
      setEditTierCredits('');
      setEditTierPrice('');
      setEditTierBadge('');
      setIsEditTierFeatured(false);
    } catch (error) {
      toast.error(`Failed to update tier: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteTier = (tierId: string, tierName: string) => {
    setTierToDelete({ id: tierId, name: tierName });
    setIsDeleteTierOpen(true);
    setOpenDropdownId(null); // Close the dropdown
  };

  const confirmDeleteTier = async () => {
    if (!tierToDelete) return;

    try {
      await deletePricingTier.mutateAsync(tierToDelete.id);
      toast.success(`Pricing tier "${tierToDelete.name}" deleted successfully`);
      setIsDeleteTierOpen(false);
      setTierToDelete(null);
    } catch (error) {
      toast.error(`Failed to delete tier: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleEditPlan = (plan: PartnerSubscriptionPlan) => {
    setEditingPlan(plan);
    setEditPlanName(plan.name);
    setEditPlanDescription(plan.description || '');
    setEditPlanCredits(plan.monthly_credits.toString());
    setEditPlanPrice(plan.price_monthly.toString());
    setEditPlanNameError(''); // Clear any previous errors
    setIsEditPlanOpen(true);
    setOpenDropdownId(null); // Close the dropdown
  };

  const handleUpdatePlan = async () => {
    if (!editingPlan || !editPlanName || !editPlanCredits || !editPlanPrice) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setIsUpdatingPlan(true);
      setEditPlanNameError(''); // Clear previous errors
      
      const updateData: UpdatePartnerSubscriptionPlanData = {
        name: editPlanName,
        description: editPlanDescription || undefined,
        monthly_credits: parseInt(editPlanCredits),
        price_monthly: parseFloat(editPlanPrice),
      };
      
      await updatePlan.mutateAsync({ id: editingPlan.id, data: updateData });
      
      // Auto-regenerate the permanent link when any plan details change
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('Plan updated, auto-regenerating link for plan:', editingPlan.plan_identifier);
          
          // First invalidate the old link
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-management?action=invalidate-link`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              plan_identifier: editingPlan.plan_identifier
            })
          });
          
          // Then generate a new link
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-management?action=generate-permanent-link`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              plan_identifier: editingPlan.plan_identifier
            })
          });
          
          if (response.ok) {
            console.log('Link auto-regenerated successfully for updated plan');
            // Ensure fresh data is available immediately after success
            try {
              await refetchSubscriptionLinks();
            } catch (e) {
              console.warn('Refetch subscription links after update failed, will retry on open');
            }
          } else {
            console.error('Failed to auto-regenerate link for updated plan');
          }
        }
      } catch (linkError) {
        console.error('Error auto-regenerating link for updated plan:', linkError);
        // Don't show error to user, just log it
      }
      
      // ALL operations complete - now show success and close modal
      toast.success(`Subscription plan "${editPlanName}" updated successfully with new checkout link`);
      
      // Reset form state and close modal after ALL operations complete
      setEditingPlan(null);
      setEditPlanName('');
      setEditPlanDescription('');
      setEditPlanCredits('');
      setEditPlanPrice('');
      setIsEditPlanOpen(false);
      
    } catch (error) {
      console.error('Update plan error:', error);
      console.log('Error message:', error instanceof Error ? error.message : 'No message');
      console.log('Error field:', (error as any).field);
      
      // Check if it's a duplicate name error
      if (error instanceof Error && 
          (error.message.includes('Plan identifier already exists') || 
           (error as any).field === 'name')) {
        console.log('Setting edit plan name error:', error.message);
        setEditPlanNameError(error.message);
        return; // Don't show toast, just show inline error
      }
      
      toast.error(`Failed to update plan: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUpdatingPlan(false);
    }
  };

  const handleViewSubscribers = (plan: PartnerSubscriptionPlan) => {
    setSelectedPlanForSubscribers(plan);
    setIsViewSubscribersOpen(true);
    setOpenDropdownId(null); // Close the dropdown
  };

  const handleDeletePlan = (planId: string, planName: string) => {
    setPlanToDelete({ id: planId, name: planName });
    setIsDeletePlanOpen(true);
    setOpenDropdownId(null); // Close the dropdown
  };

  const confirmDeletePlan = async () => {
    if (!planToDelete) return;

    try {
      await deletePlan.mutateAsync(planToDelete.id);
      toast.success(`Subscription plan "${planToDelete.name}" deleted successfully`);
      setIsDeletePlanOpen(false);
      setPlanToDelete(null);
    } catch (error) {
      toast.error(`Failed to delete plan: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleGenerateLink = async (plan: PartnerSubscriptionPlan) => {
    setOpenDropdownId(null);
    
    // Open modal immediately
    setIsGenerateLinkOpen(true);
    setIsLinkLoading(true);
    
    // Get fresh subscription links data to show current link
    try {
      const updatedData = await refetchSubscriptionLinks();
      const planWithLink = updatedData.data?.plans?.find((p: any) => p.plan_identifier === plan.plan_identifier);
      
      // Set plan data with fresh link information
      const planWithFreshData = {
        ...plan,
        stripe_checkout_link: planWithLink?.stripe_checkout_link
      };
      
      setSelectedPlanForLink(planWithFreshData);
      
      if (planWithLink?.stripe_checkout_link) {
        setGeneratedLink(planWithLink.stripe_checkout_link);
        console.log('Showing existing permanent link for plan:', plan.plan_identifier);
      } else {
        // Generate on-demand link for white-label partners
        console.log('Generating new link for plan:', plan.plan_identifier);
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) throw new Error('Not authenticated');
          
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-management?action=generate-permanent-link`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ plan_identifier: plan.plan_identifier })
          });
          
          if (response.ok) {
            const result = await response.json();
            const link = result.checkout_link || '';
            setGeneratedLink(link);
            // Refresh the data to show the new link
            try { await refetchSubscriptionLinks(); } catch {}
          } else {
            console.error('Failed to generate link:', await response.text());
            setGeneratedLink('');
          }
        } catch (linkError) {
          console.error('Error generating link:', linkError);
          setGeneratedLink('');
        }
      }
      
    } catch (error) {
      console.error('Error fetching subscription link:', error);
      // Fallback to plan data without link
      setSelectedPlanForLink(plan);
      setGeneratedLink('');
    } finally {
      setIsLinkLoading(false);
    }
  };

  // Note: generateSubscriptionLink function removed since links are now auto-generated
  // on plan creation and updates

  const copyLinkToClipboard = async () => {
    if (!generatedLink) return;
    
    try {
      await navigator.clipboard.writeText(generatedLink);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const openLinkInNewTab = () => {
    if (!generatedLink) return;
    window.open(generatedLink, '_blank');
  };

  // Loading state
  if (pricingLoading || plansLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading pricing data...</span>
      </div>
    );
  }

  // Error state
  if (pricingError || plansError) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
              Error Loading Pricing Data
            </CardTitle>
            <CardDescription>
              {pricingError?.message || plansError?.message || 'Failed to load pricing data'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => globalThis.location.reload()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const customPlans = subscriptionPlans || [];
  const boostPacks = pricingTiers || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Custom Pricing</h2>
          <p className="text-muted-foreground">
            Create custom subscription plans and boost pack pricing for your clients
          </p>
        </div>
      </div>

      {/* Subscription Plans Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Settings className="h-5 w-5 mr-2" />
                Subscription Plans
              </CardTitle>
              <CardDescription>
                Custom monthly subscription plans for your clients
              </CardDescription>
            </div>
            <CreatePlanDialog />
          </div>
        </CardHeader>
        <CardContent>
          {customPlans.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No custom subscription plans yet</p>
              <p className="text-sm">Create your first plan to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Margin</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customPlans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{plan.name}</div>
                        {plan.description && (
                          <div className="text-sm text-muted-foreground">{plan.description}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{plan.monthly_credits.toLocaleString()}</TableCell>
                    <TableCell>${plan.price_monthly}</TableCell>
                    <TableCell>
                      {calculatePlanMargin(plan.monthly_credits, plan.price_monthly)}%
                    </TableCell>
                    <TableCell>
                      <Badge variant={plan.is_active ? "default" : "secondary"}>
                        {plan.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                                         <TableCell>
                       <DropdownMenu open={openDropdownId === plan.id} onOpenChange={(open) => setOpenDropdownId(open ? plan.id : null)}>
                         <DropdownMenuTrigger asChild>
                           <Button variant="ghost" size="sm">
                             <MoreHorizontal className="h-4 w-4" />
                           </Button>
                         </DropdownMenuTrigger>
                                                   <DropdownMenuContent>
                            {isWhiteLabel && !hasCustomBoostPackPricing ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div>
                                      <DropdownMenuItem 
                                        onClick={() => handleGenerateLink(plan)}
                                        disabled={true}
                                        className="opacity-50 cursor-not-allowed"
                                      >
                                        <Link className="h-4 w-4 mr-2" />
                                        Link
                                      </DropdownMenuItem>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Configure custom boost pack pricing first</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <DropdownMenuItem 
                                onClick={() => handleGenerateLink(plan)}
                                disabled={false}
                              >
                                <Link className="h-4 w-4 mr-2" />
                                Link
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleEditPlan(plan)}>
                              <Settings className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleViewSubscribers(plan)}>
                              <Users className="h-4 w-4 mr-2" />
                              View Subscribers
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeletePlan(plan.id, plan.name)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                       </DropdownMenu>
                     </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Boost Pack Pricing Section - WHITE-LABEL ONLY */}
      {isWhiteLabel && (
        <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Zap className="h-5 w-5 mr-2" />
                Boost Pack Pricing
              </CardTitle>
              <CardDescription>
                Set your per-credit rate for boost pack purchases (wholesale cost: $0.19/credit)
              </CardDescription>
            </div>
            <Dialog open={isCreateTierOpen} onOpenChange={(open) => {
              setIsCreateTierOpen(open);
              if (!open) {
                setOpenDropdownId(null); // Reset dropdown state when dialog closes
              }
            }}>
              <DialogTrigger asChild>
                <Button disabled={boostPacks.length > 0}>
                  <Plus className="h-4 w-4 mr-2" />
                  {boostPacks.length > 0 ? 'Rate Set' : 'Set Rate'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Set Boost Pack Rate</DialogTitle>
                  <DialogDescription>
                    Set your per-credit rate for all boost pack purchases
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="creditRate">Rate per Credit (USD) *</Label>
                    <Input
                      id="creditRate"
                      type="number"
                      step="0.0001"
                      min="0.1900"
                      value={tierPrice}
                      onChange={(e) => setTierPrice(e.target.value)}
                      placeholder="0.25"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Minimum: $0.19 (wholesale cost)
                    </p>
                  </div>
                  
                  {tierPrice && parseFloat(tierPrice) >= 0.19 && (
                    <div className="p-3 bg-muted rounded-lg">
                      <div className="text-sm space-y-1">
                        <div className="font-medium">Pricing Preview:</div>
                        <div>Your Rate: ${parseFloat(tierPrice).toFixed(4)}/credit</div>
                        <div>Wholesale Cost: $0.1900/credit</div>
                        <div>Your Margin: ${(parseFloat(tierPrice) - 0.19).toFixed(4)}/credit</div>
                        <div className="pt-2 border-t">
                          <div className="font-medium">Examples:</div>
                          <div>100 credits = ${(100 * parseFloat(tierPrice)).toFixed(2)}</div>
                          <div>500 credits = ${(500 * parseFloat(tierPrice)).toFixed(2)}</div>
                          <div>1000 credits = ${(1000 * parseFloat(tierPrice)).toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => {
                      setIsCreateTierOpen(false);
                      setOpenDropdownId(null);
                    }}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateBoostPackRate} disabled={createPricingTier.isPending || !tierPrice || parseFloat(tierPrice) < 0.19}>
                      {createPricingTier.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Setting Rate...
                        </>
                      ) : (
                        'Set Rate'
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {boostPacks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No boost pack rate set</p>
              <p className="text-sm">Set your per-credit rate to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {boostPacks.map((tier) => {
                const rate = tier.price_usd; // Since credits is 1, price_usd is the per-credit rate
                const margin = rate - 0.19;
                const marginPercentage = ((margin / 0.19) * 100).toFixed(1);
                
                return (
                  <div key={tier.id} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-lg">${rate.toFixed(4)} per credit</div>
                        <div className="text-sm text-muted-foreground">
                          Margin: ${margin.toFixed(4)} per credit ({marginPercentage}% above wholesale)
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground mb-1">Examples:</div>
                        <div className="space-y-1 text-sm">
                          <div>100 credits = ${(100 * rate).toFixed(2)}</div>
                          <div>500 credits = ${(500 * rate).toFixed(2)}</div>
                          <div>1000 credits = ${(1000 * rate).toFixed(2)}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <DropdownMenu open={openDropdownId === tier.id} onOpenChange={(open) => setOpenDropdownId(open ? tier.id : null)}>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => handleEditBoostPackRate(tier)}>
                              Update Rate
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteTier(tier.id, 'boost pack rate')}
                              className="text-red-600"
                            >
                              Remove Rate
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
        </Card>
      )}

             {/* Edit Plan Dialog */}
       <Dialog open={isEditPlanOpen} onOpenChange={(open) => {
         setIsEditPlanOpen(open);
          if (!open) {
            setOpenDropdownId(null); // Reset dropdown state when dialog closes
          }
        }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Subscription Plan</DialogTitle>
            <DialogDescription>
              Update the subscription plan details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="editPlanName">Plan Name *</Label>
              <Input
                id="editPlanName"
                value={editPlanName}
                onChange={(e) => {
                  setEditPlanName(e.target.value);
                  setEditPlanNameError(''); // Clear error when user types
                }}
                placeholder="e.g., Starter Pro"
                className={editPlanNameError ? 'border-red-500' : ''}
              />
              {editPlanNameError && (
                <p className="text-sm text-red-500 mt-1">{editPlanNameError}</p>
              )}
            </div>
            <div>
              <Label htmlFor="editPlanDescription">Description</Label>
              <Textarea
                id="editPlanDescription"
                value={editPlanDescription}
                onChange={(e) => setEditPlanDescription(e.target.value)}
                placeholder="Describe the plan features..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editPlanCredits">Monthly Credits *</Label>
                <Input
                  id="editPlanCredits"
                  type="number"
                  value={editPlanCredits}
                  onChange={(e) => setEditPlanCredits(e.target.value)}
                  placeholder="500"
                />
              </div>
              <div>
                <Label htmlFor="editPlanPrice">Monthly Price ($) *</Label>
                <Input
                  id="editPlanPrice"
                  type="number"
                  step="0.01"
                  value={editPlanPrice}
                  onChange={(e) => setEditPlanPrice(e.target.value)}
                  placeholder="97.00"
                />
              </div>
            </div>
            {editPlanCredits && editPlanPrice && (
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-sm space-y-1">
                  <div>Base Cost: ${(parseInt(editPlanCredits) * 0.38).toFixed(2)}</div>
                  <div>Margin: {calculatePlanMargin(parseInt(editPlanCredits), parseFloat(editPlanPrice))}%</div>
                </div>
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsEditPlanOpen(false)} disabled={isUpdatingPlan}>
                Cancel
              </Button>
              <Button onClick={handleUpdatePlan} disabled={isUpdatingPlan}>
                {isUpdatingPlan ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Plan'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

             {/* View Subscribers Dialog */}
       <Dialog open={isViewSubscribersOpen} onOpenChange={(open) => {
         setIsViewSubscribersOpen(open);
         if (!open) {
           setOpenDropdownId(null); // Reset dropdown state when dialog closes
         }
       }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              Subscribers - {selectedPlanForSubscribers?.name}
            </DialogTitle>
            <DialogDescription>
              View all subscribers for this subscription plan
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
                         {subscribersLoading ? (
               <div className="flex items-center justify-center py-8">
                 <Loader2 className="h-6 w-6 animate-spin" />
                 <span className="ml-2">Loading subscribers...</span>
               </div>
             ) : !subscribers || subscribers.length === 0 ? (
               <div className="text-center py-8 text-muted-foreground">
                 <p>No subscribers found for this plan</p>
               </div>
             ) : (
               <Table>
                 <TableHeader>
                   <TableRow>
                     <TableHead>Name</TableHead>
                     <TableHead>Email</TableHead>
                     <TableHead>Status</TableHead>
                     <TableHead>Subscribed</TableHead>
                   </TableRow>
                 </TableHeader>
                 <TableBody>
                   {subscribers.map((subscriber: any) => (
                     <TableRow key={subscriber.id}>
                       <TableCell>{subscriber.name}</TableCell>
                       <TableCell>{subscriber.email}</TableCell>
                       <TableCell>
                         <Badge variant={subscriber.status === 'active' ? "default" : "secondary"}>
                           {subscriber.status}
                         </Badge>
                       </TableCell>
                       <TableCell>{new Date(subscriber.subscribed_at).toLocaleDateString()}</TableCell>
                     </TableRow>
                   ))}
                 </TableBody>
               </Table>
             )}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setIsViewSubscribersOpen(false)}>
                Close
              </Button>
            </div>
          </div>
                 </DialogContent>
       </Dialog>

       {/* Edit Tier Dialog */}
       <Dialog open={isEditTierOpen} onOpenChange={(open) => {
         setIsEditTierOpen(open);
         if (!open) {
           setOpenDropdownId(null); // Reset dropdown state when dialog closes
         }
       }}>
         <DialogContent className="max-w-md">
           <DialogHeader>
             <DialogTitle>Edit Pricing Tier</DialogTitle>
             <DialogDescription>
               Update the pricing tier details
             </DialogDescription>
           </DialogHeader>
           <div className="space-y-4">
             <div>
               <Label htmlFor="editTierName">Tier Name *</Label>
               <Input
                 id="editTierName"
                 value={editTierName}
                 onChange={(e) => setEditTierName(e.target.value)}
                 placeholder="e.g., Quick Refill"
               />
             </div>
             <div className="grid grid-cols-2 gap-4">
               <div>
                 <Label htmlFor="editTierCredits">Credits *</Label>
                 <Input
                   id="editTierCredits"
                   type="number"
                   value={editTierCredits}
                   onChange={(e) => setEditTierCredits(e.target.value)}
                   placeholder="50"
                 />
               </div>
               <div>
                 <Label htmlFor="editTierPrice">Price ($) *</Label>
                 <Input
                   id="editTierPrice"
                   type="number"
                   step="0.01"
                   value={editTierPrice}
                   onChange={(e) => setEditTierPrice(e.target.value)}
                   placeholder="99.00"
                 />
               </div>
             </div>
             <div>
               <Label htmlFor="editTierBadge">Badge Text</Label>
               <Input
                 id="editTierBadge"
                 value={editTierBadge}
                 onChange={(e) => setEditTierBadge(e.target.value)}
                 placeholder="e.g., Most Popular"
               />
             </div>
             <div className="flex items-center space-x-2">
               <Checkbox
                 id="isEditFeatured"
                 checked={isEditTierFeatured}
                 onCheckedChange={(checked) => setIsEditTierFeatured(checked as boolean)}
               />
               <Label htmlFor="isEditFeatured">Featured tier</Label>
             </div>
             {editTierCredits && editTierPrice && (
               <div className="p-3 bg-muted rounded-lg">
                 <div className="text-sm space-y-1">
                   <div>Base Cost: ${(parseInt(editTierCredits) * 0.19).toFixed(2)}</div>
                   <div>Margin: {calculateTierMargin(parseInt(editTierCredits), parseFloat(editTierPrice))}%</div>
                   <div>Price per Credit: ${(parseFloat(editTierPrice) / parseInt(editTierCredits)).toFixed(2)}</div>
                 </div>
               </div>
             )}
             <div className="flex justify-end space-x-2">
               <Button variant="outline" onClick={() => setIsEditTierOpen(false)}>
                 Cancel
               </Button>
               <Button onClick={handleUpdateTier} disabled={updatePricingTier.isPending}>
                 {updatePricingTier.isPending ? (
                   <>
                     <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                     Updating...
                   </>
                 ) : (
                   'Update Tier'
                 )}
               </Button>
             </div>
           </div>
         </DialogContent>
       </Dialog>

       {/* Delete Confirmation Dialog */}
       <Dialog open={isDeleteTierOpen} onOpenChange={(open) => {
         setIsDeleteTierOpen(open);
         if (!open) {
           setOpenDropdownId(null); // Reset dropdown state when dialog closes
         }
       }}>
         <DialogContent className="max-w-md">
           <DialogHeader>
             <DialogTitle>Delete Pricing Tier</DialogTitle>
             <DialogDescription>
               Are you sure you want to delete "{tierToDelete?.name}"? This action cannot be undone.
             </DialogDescription>
           </DialogHeader>
           <div className="flex justify-end space-x-2">
             <Button variant="outline" onClick={() => setIsDeleteTierOpen(false)}>
               Cancel
             </Button>
             <Button 
               variant="destructive" 
               onClick={confirmDeleteTier}
               disabled={deletePricingTier.isPending}
             >
               {deletePricingTier.isPending ? (
                 <>
                   <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                   Deleting...
                 </>
               ) : (
                 'Delete Tier'
               )}
             </Button>
           </div>
         </DialogContent>
       </Dialog>

       {/* Delete Plan Confirmation Dialog */}
       <Dialog open={isDeletePlanOpen} onOpenChange={(open) => {
         setIsDeletePlanOpen(open);
         if (!open) {
           setOpenDropdownId(null); // Reset dropdown state when dialog closes
         }
       }}>
         <DialogContent className="max-w-md">
           <DialogHeader>
             <DialogTitle>Delete Subscription Plan</DialogTitle>
             <DialogDescription>
               Are you sure you want to delete "{planToDelete?.name}"? This action cannot be undone.
             </DialogDescription>
           </DialogHeader>
           <div className="flex justify-end space-x-2">
             <Button variant="outline" onClick={() => setIsDeletePlanOpen(false)}>
               Cancel
             </Button>
             <Button 
               variant="destructive" 
               onClick={confirmDeletePlan}
               disabled={deletePlan.isPending}
             >
               {deletePlan.isPending ? (
                 <>
                   <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                   Deleting...
                 </>
               ) : (
                 'Delete Plan'
               )}
             </Button>
           </div>
         </DialogContent>
       </Dialog>

       {/* Subscription Link Dialog */}
       <Dialog open={isGenerateLinkOpen} onOpenChange={(open) => {
         setIsGenerateLinkOpen(open);
         if (!open) {
           setGeneratedLink('');
           setSelectedPlanForLink(null);
         }
       }}>
         <DialogContent className="max-w-lg">
           <DialogHeader>
             <DialogTitle className="flex items-center">
               <Link className="h-5 w-5 mr-2" />
               Subscription Link
             </DialogTitle>
             <DialogDescription>
               Subscription link for "{selectedPlanForLink?.name}" plan
             </DialogDescription>
           </DialogHeader>
           <div className="space-y-4">
             {selectedPlanForLink && (
               <div className="p-4 bg-muted rounded-lg">
                 <div className="text-sm space-y-1">
                   <div className="font-medium">{selectedPlanForLink.name}</div>
                   <div>{selectedPlanForLink.monthly_credits.toLocaleString()} credits/month</div>
                   <div>${selectedPlanForLink.price_monthly}/month</div>
                   <div className="text-green-600">
                     {isWhiteLabel ? (
                       <>
                         Your commission: ${((selectedPlanForLink.price_monthly / selectedPlanForLink.monthly_credits - 0.19) * selectedPlanForLink.monthly_credits).toFixed(2)} 
                         (${((selectedPlanForLink.price_monthly / selectedPlanForLink.monthly_credits) - 0.19).toFixed(2)}/credit markup)
                       </>
                     ) : (
                       <>
                         Your commission: ${(selectedPlanForLink.price_monthly * (partnerDetails?.commission_rate || 30) / 100).toFixed(2)} 
                         ({partnerDetails?.commission_rate || 30}% of ${selectedPlanForLink.price_monthly})
                       </>
                     )}
                   </div>
                 </div>
               </div>
             )}

             {isLinkLoading ? (
               <div className="flex items-center justify-center py-8 text-muted-foreground">
                 <Loader2 className="h-5 w-5 animate-spin mr-2" />
                 <span>Fetching subscription link...</span>
               </div>
             ) : generatedLink ? (
               <div className="space-y-3">
                 <Label>Permanent Subscription Link</Label>
                 <div className="flex gap-2">
                   <Input
                     readOnly
                     value={generatedLink}
                     className="text-xs font-mono"
                   />
                   <Button
                     variant="outline"
                     size="sm"
                     onClick={copyLinkToClipboard}
                   >
                     <Copy className="h-3 w-3" />
                   </Button>
                   <Button
                     variant="outline"
                     size="sm"
                     onClick={openLinkInNewTab}
                   >
                     <ExternalLink className="h-3 w-3" />
                   </Button>
                 </div>
                 <div className="space-y-2">
                   <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                     <div className="text-sm text-green-700">
                       <div className="font-medium">✅ Subscription Link Ready!</div>
                       <div>• Customer pays Schedule Hero AI directly</div>
                       <div>• You earn commission automatically</div>
                       <div>• This link never expires - share with your audience</div>
                       <div>• Auto-generated when plan was created/updated</div>
                     </div>
                   </div>
                 </div>
                 <div className="flex justify-end space-x-2">
                   <Button variant="outline" onClick={() => setIsGenerateLinkOpen(false)}>
                     Close
                   </Button>
                 </div>
               </div>
             ) : (
               <div className="space-y-3">
                 <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                   <div className="text-sm text-amber-700">
                     <div className="font-medium">⏳ Link Not Available</div>
                     <div>The subscription link is still being generated. Please try again in a moment.</div>
                   </div>
                 </div>
                 <div className="flex justify-end space-x-2">
                   <Button variant="outline" onClick={() => setIsGenerateLinkOpen(false)}>
                     Close
                   </Button>
                 </div>
               </div>
             )}
           </div>
         </DialogContent>
       </Dialog>
     </div>
   );
 }

export default CustomPricing;